import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ColorsConstants, dashboardCardBgColor } from '../../cssUtils/constants.js';
import {GET_SUBSCRIPTION_CALLBACK} from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider.js';

const PaymentStatusPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const subId = searchParams.get('sub_id');
  
  const [status, setStatus] = useState('processing'); // processing, active, failed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subId) {
      setStatus('failed');
      setLoading(false);
      return;
    }

    const verifyPayment = async () => {
      try {
        const tenantId = localStorage.getItem('tenant') || '';
        // Call the backend view we just made
        const response = await axios.get(`${GET_SUBSCRIPTION_CALLBACK}?sub_id=${subId}`, {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        });
        
        const cfStatus = response.data.status;
        
        if (cfStatus === 'PAID' || cfStatus === 'BANK_APPROVAL_PENDING' || cfStatus === 'ACTIVE') {
          await refreshPermissions();
          setStatus('success');
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error("Verification failed", error);
        setStatus('failed');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [subId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress size={60} sx={{ color: ColorsConstants.primaryPurple100, mb: 3 }} />
        <Typography variant="h5">Verifying your subscription...</Typography>
        <Typography variant="body2" color="text.secondary">Please do not close this window.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 500, borderRadius: 2 }}>
        
        {status === 'success' ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'green', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>Subscription Active!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your payment was successful. You now have access to all subscribed features.
            </Typography>
            <Button 
              variant="contained" 
              fullWidth
              onClick={() => navigate('/')}
              sx={{ bgcolor: ColorsConstants.primaryPurple100, py: 1.5 }}
            >
              Go to Dashboard
            </Button>
          </>
        ) : (
          <>
            <ErrorIcon sx={{ fontSize: 80, color: 'red', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>Payment Failed</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              We couldn't verify your payment. If money was deducted, it will be refunded automatically within 5-7 days.
            </Typography>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => navigate('/')} // Back to checkout
              sx={{ py: 1.5 }}
            >
              Try Again
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentStatusPage;